import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Input,
  InputNumber,
  InputPicker,
  Message,
  SelectPicker,
  Stack,
  toaster,
  Tooltip,
  Whisper,
} from 'rsuite';
import { Button as NsiButton, Modal } from '@nsi-advisory-services/components';

import {
  useCloseDates,
  useCreateProject,
  useCreateTaskOrder,
  useRates,
  useUploadProjectPhoto,
  useUpdateTaskOrder,
  useSetProjectUsers,
  useTaskOrders,
  useUpdateProject,
  useUpsertUser,
  useUpsertUserRate,
  useUsers,
  useUsersTimeOffDays,
  useUsersWorkplanForMonths,
  useTimesheetLink,
} from '../hooks/useQueries';
import ProjectLocationPicker from '../components/ProjectLocationPicker';
import usePermissions from '../hooks/usePermissions';
import ProjectRightsChecklist, { RIGHT_SHORT } from '../components/ProjectRightsChecklist';
import { useTimesheetAvailability } from '../components/TimesheetLink';
import { geocodeCity } from '../utils/locationLookup';

import { clearRsuitePickerSearchInputSoon } from '../utils/rsuitePickerSearch';

import './ProjectCreateWizardPage.css';

const HOURS_PER_DAY = 8;

const isIsoDay = (raw) => /^\d{4}-\d{2}-\d{2}$/.test(String(raw ?? '').slice(0, 10));

// Same utilization baseline as the workplan and charts pages: weekdays * 8, less close dates
// and time off, then scaled by the user's FTE.
const countWeekdays = (start, endExclusive) => {
  if (!(start instanceof Date) || !(endExclusive instanceof Date)) return 0;
  let count = 0;
  const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  while (cur.getTime() < endExclusive.getTime()) {
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6) count += 1;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
};

const weekdayHoursInMonth = (monthStartIso) => {
  if (!isIsoDay(monthStartIso)) return 0;
  const y = Number(monthStartIso.slice(0, 4));
  const m0 = Number(monthStartIso.slice(5, 7)) - 1;
  if (!Number.isInteger(y) || !Number.isInteger(m0)) return 0;
  return countWeekdays(new Date(y, m0, 1), new Date(y, m0 + 1, 1)) * HOURS_PER_DAY;
};

// Close dates are recurring month/day definitions; weekend occurrences never cost a working
// hour, so they are not subtracted.
const closeDateHoursInMonth = (monthStartIso, closeDates) => {
  if (!isIsoDay(monthStartIso)) return 0;
  const year = Number(monthStartIso.slice(0, 4));
  const month = Number(monthStartIso.slice(5, 7));
  if (!Number.isInteger(year) || !Number.isInteger(month)) return 0;
  const daysInMonth = new Date(year, month, 0).getDate();

  let count = 0;
  for (const row of Array.isArray(closeDates) ? closeDates : []) {
    const raw = String(row?.closeDate ?? row?.close_date ?? '').slice(0, 10);
    const match = raw.match(/^\d{4}-(\d{2})-(\d{2})$/);
    if (!match) continue;
    if (Number(match[1]) !== month) continue;
    const day = Number(match[2]);
    if (day > daysInMonth) continue;
    const dow = new Date(year, month - 1, day).getDay();
    if (dow === 0 || dow === 6) continue;
    count += 1;
  }
  return count * HOURS_PER_DAY;
};

const timeOffHoursByDayFromPayload = (payload) => {
  const out = {};

  const put = (rawDay, rawHours) => {
    const day = String(rawDay || '').slice(0, 10);
    if (!isIsoDay(day)) return;
    const dow = new Date(`${day}T00:00:00`).getDay();
    if (dow === 0 || dow === 6) return;
    const hours = Number(rawHours);
    out[day] = Number.isFinite(hours) && hours >= 0 ? hours : HOURS_PER_DAY;
  };

  const byDay = payload?.time_off_hours_by_day;
  if (byDay && typeof byDay === 'object') {
    Object.entries(byDay).forEach(([day, hours]) => put(day, hours));
  }
  if (Array.isArray(payload?.entries)) {
    payload.entries.forEach((entry) => put(entry?.date ?? entry?.time_off_date, entry?.hours));
  }
  const rawDays = Array.isArray(payload?.time_off_days)
    ? payload.time_off_days
    : Array.isArray(payload?.vacation_days)
      ? payload.vacation_days
      : [];
  rawDays.forEach((day) => {
    if (!Object.prototype.hasOwnProperty.call(out, String(day || '').slice(0, 10))) put(day, HOURS_PER_DAY);
  });

  return out;
};

const avatarStyleForId = (id) => {
  const n = Number(id);
  const safe = Number.isFinite(n) ? Math.abs(Math.trunc(n)) : 0;
  const hue = (safe * 137.508) % 360;
  return { background: `hsl(${hue} 70% 90%)`, borderColor: `hsl(${hue} 55% 78%)` };
};

const PURSUIT_OPTIONS = [
  { label: 'Not a pursuit', value: 'project' },
  { label: 'Pursuit', value: 'pursuit' },
];

// Initials stand in for a logo before one is chosen, the same fallback the project
// detail hero uses.
const initialsForName = (raw) => {
  const parts = String(raw || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  const first = parts[0]?.[0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || '' : '';
  const initials = `${first}${last}`.toUpperCase();
  return initials || '—';
};

const normalizeIso10 = (raw) => {
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    const yyyy = raw.getFullYear();
    const mm = String(raw.getMonth() + 1).padStart(2, '0');
    const dd = String(raw.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
  const s = String(raw ?? '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
};

const parseMaybeIsoToDate = (raw) => {
  const s = normalizeIso10(raw);
  if (!s) return null;
  const d = new Date(`${s}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const parseCoordinate = (raw) => {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const currencyInputFormatter = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return `$${n.toFixed(2)}`;
};

const currencyInputParser = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  return String(value).replace(/[^0-9.-]/g, '');
};

const budgetCurrencyFormatter = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const normalizeBudgetDigits = (raw) => {
  const digits = String(raw ?? '').replace(/[^0-9]/g, '');
  return digits;
};

const parseBudgetNumber = (raw) => {
  const digits = normalizeBudgetDigits(raw);
  if (!digits) return null;
  const n = Number(digits);
  return Number.isFinite(n) ? n : null;
};

const rateCurrencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const normalizeApiErrorMessage = (error) => {
  const raw = String(error?.message || error || '').trim();
  if (!raw) return 'Request failed.';

  const m = raw.match(/^HTTP\s+(\d+)\s*:\s*(.*)$/i);
  if (!m) return raw;

  const payload = String(m[2] || '').trim();
  try {
    const parsed = JSON.parse(payload);
    const apiMsg = String(parsed?.error || parsed?.message || '').trim();
    if (apiMsg) return apiMsg;
  } catch {
    // ignore JSON parsing failures
  }

  return payload || raw;
};

const isHttp409Error = (error) => {
  const msg = String(error?.message || error || '');
  return /HTTP\s*409\b/i.test(msg);
};

const WizardError = ({ error }) => {
  if (!error) return null;
  if (isHttp409Error(error)) return null;
  return (
    <Message type="error" showIcon>
      {normalizeApiErrorMessage(error)}
    </Message>
  );
};

export default function ProjectCreateWizardPage() {
  const navigate = useNavigate();
  const permissions = usePermissions();
  const canViewWorkplans = permissions.can('workplans.view_others');

  const { data: users = [], isLoading: usersLoading, error: usersError } = useUsers();
  const { data: ratesRes = [] } = useRates(null);

  const createProject = useCreateProject();
  const createTaskOrder = useCreateTaskOrder();
  const updateTaskOrder = useUpdateTaskOrder();
  const updateProject = useUpdateProject();
  const uploadProjectPhoto = useUploadProjectPhoto({
    onSuccess: async () => {
      toaster.push(
        <Message type="success" showIcon closable>
          Project logo uploaded.
        </Message>,
        { placement: 'topEnd', duration: 2200 }
      );
    },
  });
  const setProjectUsers = useSetProjectUsers();
  const upsertUser = useUpsertUser();
  const upsertUserRate = useUpsertUserRate();
  const timesheetAvailability = useTimesheetAvailability();
  const canAddToTimesheet =
    !timesheetAvailability.loading && timesheetAvailability.enabled && timesheetAvailability.configured;
  const timesheetLink = useTimesheetLink();

  const [step, setStep] = useState(0);
  const [projectId, setProjectId] = useState(null);
  const safeProjectId = Number.isInteger(projectId) ? projectId : null;

  // Step 1
  const [name, setName] = useState('');
  const [contractNumber, setContractNumber] = useState('');
  const [isPursuit, setIsPursuit] = useState(false);
  const [addToTimesheet, setAddToTimesheet] = useState(false);
  const [picUserId, setPicUserId] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [locationCity, setLocationCity] = useState('');
  const [locationLat, setLocationLat] = useState(null);
  const [locationLng, setLocationLng] = useState(null);
  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  const [selectedLogoPreviewUrl, setSelectedLogoPreviewUrl] = useState('');
  const [uploadedLogoUrl, setUploadedLogoUrl] = useState('');
  const [memberPickerOpen, setMemberPickerOpen] = useState(false);
  // Set when a pick should not close the menu, so several people can be added in a row.
  const keepMemberPickerOpenRef = useRef(false);
  const [logoModalOpen, setLogoModalOpen] = useState(false);
  // Rights chosen for each person added here (user id -> rights); anyone without an entry is
  // added able to view, and members already on the project keep what they have.
  const [rightsByUserId, setRightsByUserId] = useState({});
  const [rightsUserId, setRightsUserId] = useState(null);
  const [rightsDraft, setRightsDraft] = useState([]);
  const logoInputRef = useRef(null);

  // Whatever the hero and the modal should be showing: the pending local pick first, then
  // whatever was already uploaded for a project that is being resumed.
  const logoPreviewSrc = selectedLogoPreviewUrl || uploadedLogoUrl;

  // Step 2

  // Step 3
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const projectMembersPickerRef = useRef(null);

  // Step 4
  const [ratesByUserId, setRatesByUserId] = useState({});
  const [effectiveDateByUserId, setEffectiveDateByUserId] = useState({});
  // Anyone whose date the user set by hand; the rest follow the project start date.
  const editedEffectiveDatesRef = useRef(new Set());

  const [rateSourceOpen, setRateSourceOpen] = useState(false);
  const [rateSourceProjectId, setRateSourceProjectId] = useState(null);
  const [rateSourceUserId, setRateSourceUserId] = useState(null);

  // Step 3 (Task Orders)
  const [addTaskOrderOpen, setAddTaskOrderOpen] = useState(false);
  const [taskOrderModalMode, setTaskOrderModalMode] = useState('create'); // 'create' | 'edit'
  const [editingTaskOrderId, setEditingTaskOrderId] = useState(null);
  const [newTaskOrderName, setNewTaskOrderName] = useState('');
  const [newTaskOrderStartDate, setNewTaskOrderStartDate] = useState(null);
  const [newTaskOrderEndDate, setNewTaskOrderEndDate] = useState(null);
  const [newTaskOrderBudget, setNewTaskOrderBudget] = useState('');

  const userOptions = useMemo(
    () =>
      (users || []).map((u) => ({
        label: String(u?.name || '—'),
        value: Number(u?.id),
        ftePercent: Number.isFinite(Number(u?.ftePercent)) ? Number(u.ftePercent) : 100,
      })),
    [users]
  );
  const highestRateByUserId = useMemo(() => {
    const rows = Array.isArray(ratesRes)
      ? ratesRes
      : Array.isArray(ratesRes?.rows)
        ? ratesRes.rows
        : [];
    const map = new Map();

    for (const r of rows) {
      const userId = Number(r?.userId);
      const rate = Number(r?.rate);
      if (!Number.isInteger(userId) || !Number.isFinite(rate)) continue;

      const effectiveDate = String(r?.effectiveDate || '').slice(0, 10);
      const projectName = String(r?.projectName || '').trim() || 'Unknown project';
      const prev = map.get(userId);

      if (!prev || rate > prev.rate || (rate === prev.rate && effectiveDate.localeCompare(String(prev.effectiveDate || '')) > 0)) {
        map.set(userId, {
          rate,
          projectName,
          effectiveDate,
        });
      }
    }

    return map;
  }, [ratesRes]);

  // Every month the project runs through, so utilization is averaged over the whole thing
  // rather than over a single month.
  const projectMonths = useMemo(() => {
    const startIso = normalizeIso10(startDate);
    const endIso = normalizeIso10(endDate);
    if (!startIso || !endIso) return [];
    if (String(endIso).localeCompare(String(startIso)) < 0) return [];

    const out = [];
    let year = Number(startIso.slice(0, 4));
    let month = Number(startIso.slice(5, 7));
    const endYear = Number(endIso.slice(0, 4));
    const endMonth = Number(endIso.slice(5, 7));

    while ((year < endYear || (year === endYear && month <= endMonth)) && out.length < 120) {
      out.push(`${year}-${String(month).padStart(2, '0')}-01`);
      month += 1;
      if (month > 12) {
        month = 1;
        year += 1;
      }
    }

    return out;
  }, [startDate, endDate]);

  const directoryUserIds = useMemo(
    () => (users || []).map((u) => Number(u?.id)).filter((id) => Number.isInteger(id)),
    [users]
  );

  // Only fetched while the people step is open: it is one workplan and one time-off request
  // per person in the directory. Reading other people's workplans needs workplans.view_others.
  const utilizationEnabled =
    canViewWorkplans && step === 2 && projectMonths.length > 0 && directoryUserIds.length > 0;

  const { data: closeDates = [] } = useCloseDates({ enabled: utilizationEnabled });
  const workplanQueries = useUsersWorkplanForMonths(directoryUserIds, projectMonths, {
    enabled: utilizationEnabled,
  });
  const timeOffQueries = useUsersTimeOffDays(directoryUserIds, { enabled: utilizationEnabled });

  const monthCapacityBase = useMemo(() => {
    const map = new Map();
    for (const ms of projectMonths) {
      map.set(ms, Math.max(0, weekdayHoursInMonth(ms) - closeDateHoursInMonth(ms, closeDates)));
    }
    return map;
  }, [projectMonths, closeDates]);

  const utilizationByUserId = useMemo(() => {
    const map = new Map();
    if (!utilizationEnabled) return map;

    const fteById = new Map();
    for (const u of users || []) {
      const id = Number(u?.id);
      if (!Number.isInteger(id)) continue;
      const raw = Number(u?.ftePercent ?? u?.fte_percent ?? 100);
      fteById.set(id, Number.isFinite(raw) ? raw : 100);
    }

    directoryUserIds.forEach((userId, index) => {
      const workplan = workplanQueries?.[index]?.data;
      const timeOff = timeOffQueries?.[index]?.data;
      if (!workplan) return;

      const scheduledByMonth = new Map();
      for (const row of Array.isArray(workplan?.rows) ? workplan.rows : []) {
        for (const ms of projectMonths) {
          const hours = Number(row?.hours?.[ms] ?? 0);
          if (!Number.isFinite(hours) || hours <= 0) continue;
          scheduledByMonth.set(ms, (scheduledByMonth.get(ms) ?? 0) + hours);
        }
      }

      const timeOffByMonth = new Map();
      for (const [day, hours] of Object.entries(timeOffHoursByDayFromPayload(timeOff))) {
        const ms = `${day.slice(0, 7)}-01`;
        if (!monthCapacityBase.has(ms)) continue;
        timeOffByMonth.set(ms, (timeOffByMonth.get(ms) ?? 0) + Number(hours || 0));
      }

      const fteScale = Math.max(0, (fteById.get(userId) ?? 100) / 100);
      let scheduled = 0;
      let capacity = 0;

      for (const ms of projectMonths) {
        const base = Number(monthCapacityBase.get(ms) ?? 0);
        const afterTimeOff = Math.max(0, base - Number(timeOffByMonth.get(ms) ?? 0));
        capacity += afterTimeOff * fteScale;
        scheduled += Number(scheduledByMonth.get(ms) ?? 0);
      }

      if (capacity <= 0) return;
      map.set(userId, (scheduled / capacity) * 100);
    });

    return map;
  }, [
    utilizationEnabled,
    users,
    directoryUserIds,
    workplanQueries,
    timeOffQueries,
    projectMonths,
    monthCapacityBase,
  ]);

  const utilizationLoading = useMemo(
    () =>
      utilizationEnabled &&
      ([...(workplanQueries || []), ...(timeOffQueries || [])].some((q) => q?.isLoading)),
    [utilizationEnabled, workplanQueries, timeOffQueries]
  );

  const memberPickerOptions = useMemo(() => {
    return (users || []).map((u) => {
      const userId = Number(u?.id);
      const userName = String(u?.name || '—');
      const topRate = highestRateByUserId.get(userId);

      return {
        label: userName,
        value: String(userId),
        userId,
        highestRateText: topRate ? rateCurrencyFormatter.format(Number(topRate.rate)) : null,
        highestRateProjectName: topRate?.projectName || null,
      };
    });
  }, [highestRateByUserId, users]);

  const userNameById = useMemo(() => {
    const map = new Map();
    for (const u of users || []) {
      const id = Number(u?.id);
      if (!Number.isInteger(id)) continue;
      map.set(id, u?.name || '—');
    }
    return map;
  }, [users]);

  const memberPickerAvailable = useMemo(() => {
    const taken = new Set((selectedUserIds || []).map((id) => String(id)));
    return memberPickerOptions.filter((o) => o.value && o.value !== '0' && !taken.has(String(o.value)));
  }, [memberPickerOptions, selectedUserIds]);

  // Every rate already on file, keyed by project and person, newest effective date winning.
  const rateByProjectAndUser = useMemo(() => {
    const rows = Array.isArray(ratesRes) ? ratesRes : Array.isArray(ratesRes?.rows) ? ratesRes.rows : [];
    const map = new Map();

    for (const r of rows) {
      const projId = Number(r?.projectId);
      const userId = Number(r?.userId);
      const rate = Number(r?.rate);
      if (!Number.isInteger(projId) || !Number.isInteger(userId) || !Number.isFinite(rate)) continue;

      const key = `${projId}|${userId}`;
      const effectiveDate = String(r?.effectiveDate || '').slice(0, 10);
      const prev = map.get(key);
      if (!prev || effectiveDate.localeCompare(String(prev.effectiveDate || '')) > 0) {
        map.set(key, { rate, effectiveDate, projectName: r?.projectName || '' });
      }
    }

    return map;
  }, [ratesRes]);

  const rateSourceProjectOptions = useMemo(() => {
    const rows = Array.isArray(ratesRes) ? ratesRes : Array.isArray(ratesRes?.rows) ? ratesRes.rows : [];
    const byId = new Map();

    for (const r of rows) {
      const projId = Number(r?.projectId);
      if (!Number.isInteger(projId)) continue;
      if (Number.isInteger(safeProjectId) && projId === safeProjectId) continue;
      if (!byId.has(projId)) byId.set(projId, String(r?.projectName || `Project #${projId}`));
    }

    return Array.from(byId.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [ratesRes, safeProjectId]);

  // Who the chosen project can actually supply a rate for.
  const rateSourceMatches = useMemo(() => {
    const projId = Number(rateSourceProjectId);
    if (!Number.isInteger(projId)) return [];

    const ids = Number.isInteger(rateSourceUserId) ? [rateSourceUserId] : (selectedUserIds || []);
    return ids
      .map((uid) => {
        const userId = Number(uid);
        const found = rateByProjectAndUser.get(`${projId}|${userId}`);
        return found ? { userId, name: userNameById.get(userId) || '—', rate: found.rate } : null;
      })
      .filter(Boolean);
  }, [rateSourceProjectId, rateSourceUserId, selectedUserIds, rateByProjectAndUser, userNameById]);

  const openRateSource = (userId = null) => {
    setRateSourceUserId(Number.isInteger(userId) ? userId : null);
    setRateSourceProjectId(null);
    setRateSourceOpen(true);
  };

  const applyRateSource = () => {
    if (rateSourceMatches.length === 0) return;

    setRatesByUserId((prev) => {
      const next = { ...(prev || {}) };
      for (const match of rateSourceMatches) next[match.userId] = match.rate;
      return next;
    });

    setRateSourceOpen(false);
  };

  const projectPeopleDisplay = useMemo(() => {
    return (selectedUserIds || [])
      .map((id) => ({ id: Number(id), name: userNameById.get(Number(id)) || '—' }))
      .filter((u) => Number.isInteger(u.id))
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [selectedUserIds, userNameById]);


  const detailsValid = useMemo(() => {
    const n = String(name || '').trim();
    if (!n) return false;
    const s = normalizeIso10(startDate);
    const e = normalizeIso10(endDate);
    if (!s || !e) return false;
    if (String(e).localeCompare(String(s)) < 0) return false;
    return true;
  }, [name, startDate, endDate]);

  const projectStartIso = useMemo(() => normalizeIso10(startDate), [startDate]);
  const projectEndIso = useMemo(() => normalizeIso10(endDate), [endDate]);
  const shouldDisableOutsideProjectRange = useMemo(() => {
    if (!projectStartIso || !projectEndIso) return () => false;
    return (date) => {
      const iso = normalizeIso10(date);
      if (!iso) return false;
      return String(iso).localeCompare(String(projectStartIso)) < 0 || String(iso).localeCompare(String(projectEndIso)) > 0;
    };
  }, [projectStartIso, projectEndIso]);

  const handleNewTaskOrderBudgetChange = (next) => {
    const digits = normalizeBudgetDigits(next);
    if (!digits) {
      setNewTaskOrderBudget('');
      return;
    }
    setNewTaskOrderBudget(budgetCurrencyFormatter(digits));
  };

  const peopleValid = useMemo(() => Array.isArray(selectedUserIds) && selectedUserIds.length > 0, [selectedUserIds]);

  const ratesValid = useMemo(() => {
    if (!Number.isInteger(safeProjectId)) return false;
    if (!Array.isArray(selectedUserIds) || selectedUserIds.length === 0) return false;

    for (const uid of selectedUserIds) {
      const rate = Number(ratesByUserId?.[uid]);
      const eff = normalizeIso10(effectiveDateByUserId?.[uid]);
      if (!Number.isFinite(rate) || rate <= 0) return false;
      if (!eff) return false;
    }

    return true;
  }, [safeProjectId, selectedUserIds, ratesByUserId, effectiveDateByUserId]);

  const isBusy =
    createProject.isPending ||
    createTaskOrder.isPending ||
    updateTaskOrder.isPending ||
    updateProject.isPending ||
    uploadProjectPhoto.isPending ||
    setProjectUsers.isPending ||
    upsertUser.isPending ||
    upsertUserRate.isPending ||
    timesheetLink.isPending;

  useEffect(() => {
    return () => {
      if (selectedLogoPreviewUrl) {
        try {
          URL.revokeObjectURL(selectedLogoPreviewUrl);
        } catch {
          // no-op
        }
      }
    };
  }, [selectedLogoPreviewUrl]);

  // Shared by the file dialog and the modal's drop zone; the project does not exist yet,
  // so the file is only previewed here and uploaded when step 1 is submitted.
  const applyLogoFile = (file) => {
    if (!file) return;

    if (!String(file.type || '').startsWith('image/')) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Please select an image file.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Image is too large (max 5MB).
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    if (selectedLogoPreviewUrl) {
      try {
        URL.revokeObjectURL(selectedLogoPreviewUrl);
      } catch {
        // no-op
      }
    }

    setSelectedLogoFile(file);
    setSelectedLogoPreviewUrl(objectUrl);
  };

  const onSelectLogoFile = (event) => {
    applyLogoFile(event?.target?.files?.[0]);
    if (event?.target) event.target.value = '';
  };

  const clearSelectedLogo = () => {
    setSelectedLogoFile(null);
    if (selectedLogoPreviewUrl) {
      try {
        URL.revokeObjectURL(selectedLogoPreviewUrl);
      } catch {
        // no-op
      }
    }
    setSelectedLogoPreviewUrl('');
    setUploadedLogoUrl('');
  };

  const {
    data: taskOrders = [],
    isLoading: taskOrdersLoading,
    error: taskOrdersError,
  } = useTaskOrders(safeProjectId, { enabled: Number.isInteger(safeProjectId) });

  const nonDefaultTaskOrders = useMemo(() => {
    return (Array.isArray(taskOrders) ? taskOrders : []).filter((o) => !o?.isDefault);
  }, [taskOrders]);

  useEffect(() => {
    // Keep PIC in the selected list by default.
    const pid = Number(picUserId);
    if (!Number.isInteger(pid) || pid <= 0) return;
    setSelectedUserIds((prev) => {
      const set = new Set((prev || []).map((v) => Number(v)).filter((n) => Number.isInteger(n)));
      set.add(pid);
      return Array.from(set);
    });
  }, [picUserId]);

  useEffect(() => {
    // Effective dates default to the project start. The PIC is selected before the dates are
    // entered, so untouched rows are re-seeded whenever the start date changes rather than
    // keeping whatever they were first given.
    const defaultEff = normalizeIso10(startDate);
    if (!defaultEff) return;

    setEffectiveDateByUserId((prev) => {
      const next = { ...(prev || {}) };
      let changed = false;

      for (const uid of selectedUserIds || []) {
        if (editedEffectiveDatesRef.current.has(Number(uid))) continue;
        if (next[uid] === defaultEff) continue;
        next[uid] = defaultEff;
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [selectedUserIds, startDate]);

  const goBackToProjects = () => navigate('/', { replace: true });

  const submitDetails = async () => {
    if (!detailsValid) return;

    const trimmed = String(name || '').trim();
    const contractNumberTrimmed = String(contractNumber || '').trim();
    const startIso = normalizeIso10(startDate);
    const endIso = normalizeIso10(endDate);

    const pic = Number.isInteger(Number(picUserId)) ? Number(picUserId) : null;

    const resolveLocationForSave = async () => {
      const cityText = String(locationCity || '').trim() || null;
      const latN = parseCoordinate(locationLat);
      const lngN = parseCoordinate(locationLng);
      const hasCoords = Number.isFinite(latN) && Number.isFinite(lngN);

      if (hasCoords || !cityText) {
        return {
          city: cityText,
          lat: hasCoords ? latN : null,
          lng: hasCoords ? lngN : null,
        };
      }

      const match = await geocodeCity(cityText);
      if (!match) {
        return { city: cityText, lat: null, lng: null };
      }

      setLocationLat(match.lat);
      setLocationLng(match.lng);
      return { city: cityText, lat: Number(match.lat), lng: Number(match.lng) };
    };

    try {
      // Create project once, then update remaining fields.
      const resolvedLocation = await resolveLocationForSave();

      let pid = safeProjectId;
      const isNewProject = !Number.isInteger(pid);
      if (isNewProject) {
        const created = await createProject.mutateAsync({
          name: trimmed,
          picUserId: pic,
          contractNumber: contractNumberTrimmed || null,
          isPursuit,
          locationCity: resolvedLocation.city,
          locationLat: resolvedLocation.lat,
          locationLng: resolvedLocation.lng,
        });
        const createdId = Number(created?.id);
        if (Number.isInteger(createdId)) {
          pid = createdId;
          setProjectId(createdId);
        }
        toaster.push(
          <Message type="success" showIcon closable>
            Project created.
          </Message>,
          { placement: 'topEnd', duration: 2200 }
        );
      }

      if (Number.isInteger(pid)) {
        await updateProject.mutateAsync({
          projectId: pid,
          payload: {
            name: trimmed,
            picUserId: pic,
            contractNumber: contractNumberTrimmed || null,
            isPursuit,
            startDate: startIso,
            endDate: endIso,
            locationCity: resolvedLocation.city,
            locationLat: resolvedLocation.lat,
            locationLng: resolvedLocation.lng,
          },
        });

        if (selectedLogoFile) {
          const toBase64 = () =>
            new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const full = String(reader.result || '');
                const commaIdx = full.indexOf(',');
                const b64 = commaIdx >= 0 ? full.slice(commaIdx + 1) : full;
                resolve(b64);
              };
              reader.onerror = () => reject(new Error('Failed to read file'));
              reader.readAsDataURL(selectedLogoFile);
            });

          const dataBase64 = await toBase64();
          const uploaded = await uploadProjectPhoto.mutateAsync({
            projectId: pid,
            payload: {
              fileName: selectedLogoFile.name,
              mimeType: selectedLogoFile.type || 'application/octet-stream',
              dataBase64,
            },
          });
          setUploadedLogoUrl(String(uploaded?.photoUrl || ''));
          setSelectedLogoFile(null);
          if (selectedLogoPreviewUrl) {
            try {
              URL.revokeObjectURL(selectedLogoPreviewUrl);
            } catch {
              // no-op
            }
          }
          setSelectedLogoPreviewUrl('');
        }

        // The project exists either way; a timesheet problem is reported and can be fixed from the
        // project page.
        if (isNewProject && addToTimesheet && canAddToTimesheet) {
          try {
            await timesheetLink.mutateAsync({ projectId: pid, action: 'create' });
            toaster.push(
              <Message type="success" showIcon closable>
                Added to the EmployeeConnect timesheet.
              </Message>,
              { placement: 'topEnd', duration: 2200 }
            );
          } catch (timesheetErr) {
            toaster.push(
              <Message type="warning" showIcon closable>
                The project was created, but it couldn&apos;t be added to the timesheet: {String(timesheetErr?.message || timesheetErr)}
                {' '}You can add it from the project page.
              </Message>,
              { placement: 'topEnd', duration: 8000 }
            );
          }
        }
      }

      setStep(1);
    } catch (err) {
      const msg = normalizeApiErrorMessage(err);
      const isDuplicateName =
        msg.includes('A project with this name already exists') ||
        msg.includes('projects_name_uq') ||
        msg.includes('duplicate key value');

      toaster.push(
        <Message type="error" showIcon closable>
          {isDuplicateName ? 'A project with this name already exists.' : `Failed to create project: ${msg}`}
        </Message>,
        { placement: 'topEnd', duration: isDuplicateName ? 3000 : 6000 }
      );
    }
  };

  const submitTaskOrders = async () => {
    if (!Number.isInteger(safeProjectId)) return;
    if (!taskOrdersValid) return;

    // Whatever the task orders add up to is the project's budget, so it is written here
    // rather than typed in on the details step. Task orders are optional though: skipping
    // the step must not zero out a budget the project already carries, so an empty sum is
    // never written.
    if (nonDefaultTaskOrders.length > 0) {
      await updateProject.mutateAsync({
        projectId: safeProjectId,
        payload: { budgetTarget: taskOrdersBudgetTotal },
      });
    }

    setStep(2);
  };

  const submitPeople = async () => {
    if (!Number.isInteger(safeProjectId)) return;
    if (!peopleValid) return;

    const ids = Array.from(
      new Set(
        (selectedUserIds || [])
          .map((v) => Number(v))
          .filter((n) => Number.isInteger(n) && n > 0)
      )
    );

    const rights = Object.fromEntries(
      ids.filter((id) => Array.isArray(rightsByUserId[id])).map((id) => [id, rightsByUserId[id]])
    );
    await setProjectUsers.mutateAsync({ projectId: safeProjectId, userIds: ids, rights });

    toaster.push(
      <Message type="success" showIcon closable>
        Project members saved.
      </Message>,
      { placement: 'topEnd', duration: 2200 }
    );

    setStep(3);
  };

  const handleCreateUserFromPicker = async (_value, item) => {
    const label = String(item?.label ?? item?.value ?? '').trim();
    if (!label) return;

    try {
      const created = await upsertUser.mutateAsync({ name: label, ftePercent: 100 });
      const id = Number(created?.id);
      if (Number.isInteger(id) && id > 0) {
        setSelectedUserIds((prev) => Array.from(new Set([...(prev || []), id])));
      }
      toaster.push(
        <Message type="success" showIcon closable>
          User added.
        </Message>,
        { placement: 'topEnd', duration: 2200 }
      );
    } catch (err) {
      toaster.push(
        <Message type="error" showIcon closable>
          Failed to create user: {String(err?.message || err)}
        </Message>,
        { placement: 'topEnd' }
      );
    }
  };

  const submitRates = async () => {
    if (!Number.isInteger(safeProjectId)) return;
    if (!ratesValid) return;

    const pid = safeProjectId;

    await Promise.all(
      (selectedUserIds || [])
        .map((v) => Number(v))
        .filter((n) => Number.isInteger(n) && n > 0)
        .map(async (userId) => {
          const rate = Number(ratesByUserId?.[userId]);
          const effectiveDate = normalizeIso10(effectiveDateByUserId?.[userId]);
          await upsertUserRate.mutateAsync({ userId, projectId: pid, rate, effectiveDate });
        })
    );

    toaster.push(
      <Message type="success" showIcon closable>
        Rates saved.
      </Message>,
      { placement: 'topEnd', duration: 2200 }
    );

    navigate(`/projects/${pid}`, { replace: true });
  };

  const resetTaskOrderModal = () => {
    setTaskOrderModalMode('create');
    setEditingTaskOrderId(null);
    setNewTaskOrderName('');
    setNewTaskOrderStartDate(null);
    setNewTaskOrderEndDate(null);
    setNewTaskOrderBudget('');
  };

  const openCreateTaskOrderModal = () => {
    resetTaskOrderModal();
    setTaskOrderModalMode('create');
    setAddTaskOrderOpen(true);
  };

  const openEditTaskOrderModal = (taskOrder) => {
    const id = Number(taskOrder?.id);
    if (!Number.isInteger(id)) return;
    setTaskOrderModalMode('edit');
    setEditingTaskOrderId(id);
    setNewTaskOrderName(String(taskOrder?.name ?? ''));
    setNewTaskOrderStartDate(taskOrder?.startDate ?? null);
    setNewTaskOrderEndDate(taskOrder?.endDate ?? null);
    setNewTaskOrderBudget(
      taskOrder?.budgetTarget === null || taskOrder?.budgetTarget === undefined
        ? ''
        : budgetCurrencyFormatter(taskOrder.budgetTarget)
    );
    setAddTaskOrderOpen(true);
  };

  const canSubmitNewTaskOrder = useMemo(() => {
    const name = String(newTaskOrderName || '').trim();
    if (!name) return false;
    const s = normalizeIso10(newTaskOrderStartDate);
    const e = normalizeIso10(newTaskOrderEndDate);
    if (!s || !e) return false;
    if (String(e).localeCompare(String(s)) < 0) return false;
    if (projectStartIso && String(s).localeCompare(String(projectStartIso)) < 0) return false;
    if (projectEndIso && String(e).localeCompare(String(projectEndIso)) > 0) return false;
    const budget = parseBudgetNumber(newTaskOrderBudget);
    if (budget === null) return false;
    if (!Number.isFinite(budget) || budget < 0) return false;

    return true;
  }, [
    newTaskOrderName,
    newTaskOrderStartDate,
    newTaskOrderEndDate,
    newTaskOrderBudget,
    projectStartIso,
    projectEndIso,
  ]);

  // The project's budget is whatever its task orders add up to.
  const taskOrdersBudgetTotal = useMemo(() => {
    return (nonDefaultTaskOrders || []).reduce((sum, o) => {
      const b = o?.budgetTarget;
      const n = b === null || b === undefined ? 0 : Number(b);
      return Number.isFinite(n) ? sum + n : sum;
    }, 0);
  }, [nonDefaultTaskOrders]);

  // Nothing may move on until every task order carries the dates and budget the rest of
  // the app forecasts against.
  const taskOrdersValid = useMemo(() => {
    return (nonDefaultTaskOrders || []).every((o) => {
      if (!normalizeIso10(o?.startDate) || !normalizeIso10(o?.endDate)) return false;
      const b = o?.budgetTarget;
      if (b === null || b === undefined || b === '') return false;
      const n = Number(b);
      return Number.isFinite(n) && n >= 0;
    });
  }, [nonDefaultTaskOrders]);

  const shouldDisableStartDate = useMemo(() => {
    const base = shouldDisableOutsideProjectRange;
    const endIso = normalizeIso10(newTaskOrderEndDate);
    if (!endIso) return base;
    return (date) => {
      if (base(date)) return true;
      const iso = normalizeIso10(date);
      if (!iso) return false;
      return String(iso).localeCompare(String(endIso)) > 0;
    };
  }, [shouldDisableOutsideProjectRange, newTaskOrderEndDate]);

  const shouldDisableEndDate = useMemo(() => {
    const base = shouldDisableOutsideProjectRange;
    const startIso = normalizeIso10(newTaskOrderStartDate);
    if (!startIso) return base;
    return (date) => {
      if (base(date)) return true;
      const iso = normalizeIso10(date);
      if (!iso) return false;
      return String(iso).localeCompare(String(startIso)) < 0;
    };
  }, [shouldDisableOutsideProjectRange, newTaskOrderStartDate]);

  const submitNewTaskOrder = async () => {
    if (!Number.isInteger(safeProjectId)) return;
    if (!canSubmitNewTaskOrder) return;

    const name = String(newTaskOrderName || '').trim();
    const startIso = normalizeIso10(newTaskOrderStartDate);
    const endIso = normalizeIso10(newTaskOrderEndDate);
    const budgetTarget = parseBudgetNumber(newTaskOrderBudget);

    if (taskOrderModalMode === 'edit') {
      const toid = Number(editingTaskOrderId);
      if (!Number.isInteger(toid)) return;
      await updateTaskOrder.mutateAsync({
        projectId: safeProjectId,
        taskOrderId: toid,
        payload: {
          name,
          startDate: startIso,
          endDate: endIso,
          budgetTarget,
        },
      });
    } else {
      await createTaskOrder.mutateAsync({
        projectId: safeProjectId,
        payload: {
          name,
          startDate: startIso,
          endDate: endIso,
          budgetTarget,
        },
      });
    }

    toaster.push(
      <Message type="success" showIcon closable>
        {taskOrderModalMode === 'edit' ? 'Task order updated.' : 'Task order created.'}
      </Message>,
      { placement: 'topEnd', duration: 2200 }
    );

    setAddTaskOrderOpen(false);
    resetTaskOrderModal();
  };

  const stepItems = [
    { title: 'Details' },
    { title: 'Task Orders' },
    { title: 'People' },
    { title: 'Rates' },
  ];

  return (
    <div className="ppc-page ppc-project-wizard-page">
      <div className="ppc-project-wizard-shell">
        <div className="ppc-project-wizard-head">
          <div className="ppc-project-wizard-head__left">
            <div className="ppc-project-wizard-crumbs" aria-label="Breadcrumb">
              <button
                type="button"
                className="ppc-project-wizard-crumb-link"
                onClick={() => navigate('/')}
              >
                Projects
              </button>
              <i className="bi bi-chevron-right ppc-project-wizard-crumb-sep" aria-hidden="true" />
              <span className="ppc-project-wizard-crumb-current">Create New Project</span>
            </div>
            <h2 className="ppc-project-wizard-title">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                <i className="bi bi-folder-plus" aria-hidden="true" />
                Create New Project
              </span>
            </h2>
          </div>

          <div className="ppc-project-wizard-head__actions">
            <Button className="ppc-project-wizard__btn" onClick={goBackToProjects} disabled={isBusy}>
              <i className="bi bi-arrow-left" aria-hidden="true" />
              Back to projects
            </Button>
          </div>
        </div>

        <Divider style={{ margin: '6px 0 14px' }} />

      <div className="ppc-project-wizard">
        <div className="ppc-project-wizard__flow" aria-label="Project wizard process flow">
          {stepItems.map((s, index) => {
            const isDone = index < step;
            const isCurrent = index === step;
            return (
              <div
                key={s.title}
                className={`ppc-project-wizard__flow-item${isDone ? ' is-done' : ''}${isCurrent ? ' is-current' : ''}`}
              >
                <div className="ppc-project-wizard__flow-node">{index + 1}</div>
                <div className="ppc-project-wizard__flow-label">{s.title}</div>
              </div>
            );
          })}
        </div>

        {usersError ? <Message type="error" showIcon>Failed to load users.</Message> : null}

        <div className="ppc-project-wizard__body">
          {step === 0 ? (
            <section className="ppc-project-wizard__section">
              <div className="ppc-project-wizard__section-head">
                <h3 className="ppc-project-wizard__section-title">Project details</h3>
              </div>

              {/* The name leads, centered above the rest; everything else pairs off below. */}
              <div className="ppc-project-wizard__hero">
                <div className="ppc-project-wizard__hero-name">
                  <span className="ppc-project-wizard__label">Project name</span>
                  <Input value={name} onChange={setName} placeholder="e.g. ProPlan 2026" disabled={isBusy} />
                </div>

                <div className="ppc-project-wizard__hero-stats">
                  <div>
                    <span className="ppc-project-wizard__label">PIC</span>
                    <SelectPicker
                      cleanable
                      searchable
                      placeholder={usersLoading ? 'Loading…' : 'Select a PIC'}
                      data={userOptions}
                      renderMenuItem={(label, item) => {
                        const userId = Number(item?.value);
                        const util = Number.isFinite(Number(item?.ftePercent)) ? Number(item.ftePercent) : 100;
                        const topRate = highestRateByUserId.get(userId);
                        const rateText = topRate ? rateCurrencyFormatter.format(Number(topRate.rate)) : null;
                        const rateProject = topRate?.projectName || null;

                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            <div style={{ fontWeight: 700, lineHeight: '16px' }}>{label}</div>
                            <div style={{ fontSize: 11, color: 'var(--pcw-text-muted)', lineHeight: '14px' }}>
                              Utilization: {util}%
                              {rateText && rateProject ? ` • Highest rate: ${rateText} (${rateProject})` : ''}
                            </div>
                          </div>
                        );
                      }}
                      value={Number.isInteger(Number(picUserId)) ? Number(picUserId) : null}
                      onChange={(v) => {
                        if (v === null || v === undefined || v === '') {
                          setPicUserId(null);
                          return;
                        }
                        const n = Number(v);
                        setPicUserId(Number.isInteger(n) ? n : null);
                      }}
                      disabled={usersLoading}
                      block
                    />
                  </div>

                  <div>
                    <span className="ppc-project-wizard__label">Contract number</span>
                    <Input
                      value={contractNumber}
                      onChange={setContractNumber}
                      placeholder="e.g. CN-2026-001"
                      disabled={isBusy}
                    />
                  </div>

                  <div>
                    <span className="ppc-project-wizard__label">Start date</span>
                    <DatePicker
                      oneTap
                      format="yyyy-MM-dd"
                      ranges={[]}
                      value={parseMaybeIsoToDate(startDate) || startDate}
                      onChange={setStartDate}
                      placeholder="YYYY-MM-DD"
                      style={{ width: '100%' }}
                      disabled={isBusy}
                    />
                  </div>

                  <div>
                    <span className="ppc-project-wizard__label">End date</span>
                    <DatePicker
                      oneTap
                      format="yyyy-MM-dd"
                      ranges={[]}
                      value={parseMaybeIsoToDate(endDate) || endDate}
                      onChange={setEndDate}
                      placeholder="YYYY-MM-DD"
                      style={{ width: '100%' }}
                      disabled={isBusy}
                    />
                  </div>

                  <div>
                    <span className="ppc-project-wizard__label">Pursuit</span>
                    <SelectPicker
                      block
                      cleanable={false}
                      searchable={false}
                      data={PURSUIT_OPTIONS}
                      value={isPursuit ? 'pursuit' : 'project'}
                      onChange={(v) => setIsPursuit(v === 'pursuit')}
                      disabled={isBusy}
                    />
                  </div>

                  <div className="ppc-project-wizard__hero-stat--wide ppc-project-wizard__hero-locationrow">
                    <div className="ppc-project-wizard__hero-location">
                      <span className="ppc-project-wizard__label">Location</span>
                      <ProjectLocationPicker
                        city={locationCity}
                        latitude={locationLat}
                        longitude={locationLng}
                        onCityChange={setLocationCity}
                        onCoordinatesChange={(lat, lng) => {
                          setLocationLat(lat);
                          setLocationLng(lng);
                        }}
                        disabled={isBusy}
                      />
                    </div>

                    <div className="ppc-project-wizard__hero-logo-cell">
                      <div>
                        <span className="ppc-project-wizard__label">Logo</span>
                        <div className="ppc-project-wizard__hero-logo">
                          <div className="ppc-project-wizard__hero-logo-media">
                            {logoPreviewSrc ? (
                              <img src={logoPreviewSrc} alt="" />
                            ) : (
                              <span className="ppc-project-wizard__hero-logo-fallback" aria-hidden="true">
                                {initialsForName(name)}
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            className="ppc-project-wizard__photo-edit-fab"
                            onClick={() => setLogoModalOpen(true)}
                            title="Edit project logo"
                            aria-label="Edit project logo"
                            disabled={isBusy}
                          >
                            <i className="bi bi-pencil" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {!detailsValid ? (
                <div style={{ marginTop: 12 }}>
                  <Message type="warning" showIcon>
                    Enter a name and valid start/end dates.
                  </Message>
                </div>
              ) : null}

              {canAddToTimesheet && !Number.isInteger(safeProjectId) ? (
                <div style={{ marginTop: 12 }}>
                  <Checkbox
                    checked={addToTimesheet}
                    onChange={(_value, checked) => setAddToTimesheet(Boolean(checked))}
                    disabled={isBusy}
                  >
                    Also add it to the EmployeeConnect timesheet
                  </Checkbox>
                </div>
              ) : null}

              <div className="ppc-project-wizard__footer">
                <WizardError error={createProject.error || updateProject.error} />
                <Stack justifyContent="flex-end" spacing={10}>
                  <Button className="ppc-project-wizard__btn ppc-project-wizard__btn--primary" onClick={submitDetails} loading={isBusy} disabled={!detailsValid || isBusy}>
                    {Number.isInteger(safeProjectId) ? 'Save & Continue' : 'Create & Continue'}
                  </Button>
                </Stack>
              </div>
            </section>
          ) : null}

          {step === 1 ? (
            <section className="ppc-project-wizard__section">
              <div className="ppc-project-wizard__section-head">
                <h3 className="ppc-project-wizard__section-title">
                  Task orders
                  <Whisper
                    placement="bottom"
                    trigger="hover"
                    speaker={
                      <Tooltip className="ppc-project-wizard__tip">
                        Projects are often split into task orders. Task orders let you split a project into phases
                        while keeping the basic info. Each task order gets its own budget, forecast, and set of
                        disciplines. If your project is only one phase, just keep the default project task order and
                        the project will not be split. You can add new task orders at any time.
                      </Tooltip>
                    }
                  >
                    <i className="bi bi-info-circle ppc-project-wizard__title-info" />
                  </Whisper>
                </h3>
                <Button
                  className="ppc-project-wizard__btn ppc-project-wizard__btn--sm"
                  onClick={() => {
                    openCreateTaskOrderModal();
                  }}
                  disabled={!Number.isInteger(safeProjectId) || isBusy}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    Add new
                    <i className="bi bi-plus-lg" aria-hidden="true" />
                  </span>
                </Button>
              </div>
              {!Number.isInteger(safeProjectId) ? (
                <Message type="info" showIcon>
                  Create the project first.
                </Message>
              ) : null}

              {taskOrdersError ? (
                <Message type="error" showIcon>
                  Failed to load task orders.
                </Message>
              ) : null}

              {taskOrdersLoading ? <div>Loading task orders…</div> : null}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {nonDefaultTaskOrders.length === 0 ? (
                  <Message type="info" showIcon>
                    No task orders yet. If you skip this step, the project will use a single implicit “Default” task order.
                  </Message>
                ) : (
                  nonDefaultTaskOrders.map((o) => {
                    const hasDates = Boolean(o?.startDate && o?.endDate);
                    const hasBudget = o?.budgetTarget !== null && o?.budgetTarget !== undefined && o?.budgetTarget !== '';

                    return (
                      <div key={String(o?.id)} className="ppc-project-wizard__task-order-row">
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 800, lineHeight: '18px' }}>{o?.name || '—'}</div>
                          <div style={{ fontSize: 12, color: 'var(--pcw-text-muted)', marginTop: 2 }}>
                            <span className={hasDates ? undefined : 'ppc-project-wizard__missing'}>
                              {hasDates ? `${o.startDate} → ${o.endDate}` : 'Dates not set'}
                            </span>
                            {' • '}
                            <span className={hasBudget ? undefined : 'ppc-project-wizard__missing'}>
                              {hasBudget ? `Budget ${budgetCurrencyFormatter(o.budgetTarget)}` : 'Budget not set'}
                            </span>
                          </div>
                        </div>
                        <Button
                          className="ppc-project-wizard__btn ppc-project-wizard__btn--sm"
                          onClick={() => openEditTaskOrderModal(o)}
                          disabled={isBusy}
                        >
                          Edit
                        </Button>
                      </div>
                    );
                  })
                )}
              </div>

              {nonDefaultTaskOrders.length > 0 ? (
                <div className="ppc-project-wizard__budget-total">
                  <div>
                    <div className="ppc-project-wizard__label">Project budget</div>
                    <div className="ppc-project-wizard__budget-total-note">
                      {`Sum of ${nonDefaultTaskOrders.length} task order${nonDefaultTaskOrders.length === 1 ? '' : 's'}`}
                    </div>
                  </div>
                  <div className="ppc-project-wizard__budget-total-value">
                    {budgetCurrencyFormatter(taskOrdersBudgetTotal)}
                  </div>
                </div>
              ) : null}

              {!taskOrdersValid ? (
                <Message type="warning" showIcon>
                  Every task order needs a start date, an end date, and a budget.
                </Message>
              ) : null}

              <div className="ppc-project-wizard__footer">
                <WizardError error={createTaskOrder.error} />
                <Stack justifyContent="space-between" spacing={10}>
                  <Button className="ppc-project-wizard__btn" onClick={() => setStep(0)} disabled={isBusy}>
                    Back
                  </Button>
                  <Button className="ppc-project-wizard__btn ppc-project-wizard__btn--primary" onClick={submitTaskOrders} loading={isBusy} disabled={!taskOrdersValid || isBusy}>
                    Save & Continue
                  </Button>
                </Stack>
              </div>
            </section>
          ) : null}

          {step === 2 ? (
            <section className="ppc-project-wizard__section">
              <div className="ppc-project-wizard__section-head">
                <h3 className="ppc-project-wizard__section-title">Assign people</h3>
                <span className="ppc-project-wizard__count">{projectPeopleDisplay.length}</span>
              </div>
              <div>
                <div className="ppc-project-wizard__label">Add project members</div>
                <InputPicker
                  ref={projectMembersPickerRef}
                  searchable
                  cleanable={false}
                  creatable
                  size="sm"
                  style={{ width: 280 }}
                  loading={usersLoading || upsertUser.isPending}
                  placeholder={usersLoading ? 'Loading…' : 'Add a person'}
                  data={memberPickerAvailable}
                  renderMenuItem={(label, item) => {
                    const util = utilizationByUserId.get(Number(item?.userId));
                    const utilText = Number.isFinite(util)
                      ? `${util.toFixed(0)}% utilized over project duration`
                      : utilizationLoading
                        ? 'Utilization…'
                        : 'Utilization: —';
                    const rateText = item?.highestRateText;
                    const projectName = item?.highestRateProjectName;

                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <div style={{ fontWeight: 700, lineHeight: '16px' }}>{label}</div>
                        <div style={{ fontSize: 11, color: 'var(--pcw-text-muted)', lineHeight: '14px' }}>
                          {utilText}
                          {rateText && projectName ? ` • Highest rate: ${rateText} (${projectName})` : ' • Highest rate: —'}
                        </div>
                      </div>
                    );
                  }}
                  // The picker is an add control, never a value: each pick lands in the list
                  // below and the field resets itself.
                  value={null}
                  open={memberPickerOpen}
                  onOpen={() => setMemberPickerOpen(true)}
                  onClose={() => {
                    // rsuite closes the menu on every pick; the list stays up until the user
                    // clicks away themselves.
                    if (keepMemberPickerOpenRef.current) {
                      keepMemberPickerOpenRef.current = false;
                      return;
                    }
                    setMemberPickerOpen(false);
                  }}
                  onChange={(v) => {
                    const id = Number(v);
                    if (Number.isInteger(id) && id > 0) {
                      keepMemberPickerOpenRef.current = true;
                      setSelectedUserIds((prev) => Array.from(new Set([...(prev || []), id])));
                    }
                    clearRsuitePickerSearchInputSoon(projectMembersPickerRef);
                  }}
                  onCreate={(v, item) => {
                    keepMemberPickerOpenRef.current = true;
                    clearRsuitePickerSearchInputSoon(projectMembersPickerRef);
                    handleCreateUserFromPicker(v, item);
                  }}
                  disabled={usersLoading}
                />

                {projectPeopleDisplay.length > 0 ? (
                  <div className="ppc-project-wizard__people-grid">
                    {projectPeopleDisplay.map((p) => {
                      const avatar = avatarStyleForId(p.id);
                      const util = utilizationByUserId.get(p.id);

                      return (
                        <div key={p.id} className="ppc-project-wizard__person">
                          <span
                            className="ppc-project-wizard__person-avatar"
                            style={{ background: avatar.background, borderColor: avatar.borderColor }}
                            aria-hidden="true"
                          >
                            {initialsForName(p.name)}
                          </span>
                          <span className="ppc-project-wizard__person-text">
                            <span className="ppc-project-wizard__person-name" title={p.name}>{p.name}</span>
                            <span className="ppc-project-wizard__person-util">
                              {Number.isFinite(util)
                                ? `${util.toFixed(0)}% utilized over project duration`
                                : 'Utilization —'}
                            </span>
                          </span>
                          <button
                            type="button"
                            className="ppc-project-wizard__person-remove ppc-project-wizard__person-rights"
                            onClick={() => {
                              setRightsUserId(p.id);
                              setRightsDraft(rightsByUserId[p.id] || []);
                            }}
                            disabled={isBusy}
                            title={`${p.name}'s rights: ${
                              (rightsByUserId[p.id] || []).map((key) => RIGHT_SHORT[key] || key).join(', ') || 'view only'
                            }`}
                            aria-label={`Set ${p.name}'s rights on this project`}
                          >
                            <i className="bi bi-shield-lock" aria-hidden="true" />
                          </button>
                          {/* The PIC is always on the project, so they cannot be removed. */}
                          {p.id !== Number(picUserId) ? (
                            <button
                              type="button"
                              className="ppc-project-wizard__person-remove"
                              onClick={() => setSelectedUserIds((prev) => (prev || []).filter((id) => Number(id) !== p.id))}
                              disabled={isBusy}
                              title={`Remove ${p.name} from this project`}
                              aria-label={`Remove ${p.name} from this project`}
                            >
                              <i className="bi bi-x-lg" aria-hidden="true" />
                            </button>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : null}

                {!peopleValid ? (
                  <div style={{ marginTop: 10 }}>
                    <Message type="warning" showIcon>
                      Select at least one person.
                    </Message>
                  </div>
                ) : null}
              </div>

              <div className="ppc-project-wizard__footer">
                <WizardError error={setProjectUsers.error} />
                <Stack justifyContent="space-between" spacing={10}>
                  <Button className="ppc-project-wizard__btn" onClick={() => setStep(1)} disabled={isBusy}>
                    Back
                  </Button>
                  <Button className="ppc-project-wizard__btn ppc-project-wizard__btn--primary" onClick={submitPeople} loading={isBusy} disabled={!peopleValid || isBusy}>
                    Save & Continue
                  </Button>
                </Stack>
              </div>
            </section>
          ) : null}

          {step === 3 ? (
            <section className="ppc-project-wizard__section">
              <div className="ppc-project-wizard__section-head">
                <h3 className="ppc-project-wizard__section-title">Assign rates</h3>
              </div>
       

              <div className="ppc-project-wizard__tablewrap">
                <table className="ppc-project-wizard__table">
                  <thead>
                    <tr>
                      <th scope="col">Person</th>
                      <th scope="col">Rate</th>
                      <th scope="col">Effective date</th>
                      <th scope="col" />
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedUserIds || []).length === 0 ? (
                      <tr>
                        <td className="ppc-project-wizard__table-empty" colSpan={4}>
                          No one is assigned to this project yet.
                        </td>
                      </tr>
                    ) : null}

                    {(selectedUserIds || []).map((uid) => {
                      const userId = Number(uid);
                      const userName = userNameById.get(userId) || '—';
                      const avatar = avatarStyleForId(userId);

                      const rateVal = ratesByUserId?.[userId] ?? '';
                      const effVal = effectiveDateByUserId?.[userId] ?? normalizeIso10(startDate) ?? '';

                      const hasRate = Number.isFinite(Number(rateVal)) && Number(rateVal) > 0;
                      const hasEff = Boolean(normalizeIso10(effVal));

                      return (
                        <tr key={String(userId)}>
                          <td>
                            <span className="ppc-project-wizard__table-person">
                              <span
                                className="ppc-project-wizard__person-avatar"
                                style={{ background: avatar.background, borderColor: avatar.borderColor }}
                                aria-hidden="true"
                              >
                                {initialsForName(userName)}
                              </span>
                              <span className="ppc-project-wizard__person-name" title={userName}>{userName}</span>
                            </span>
                          </td>
                          <td>
                            <span className={hasRate ? undefined : 'ppc-project-wizard__invalid'}>
                              <InputNumber
                                style={{ width: 160 }}
                              min={0}
                              step={1}
                              precision={2}
                              value={rateVal}
                              onChange={(v) =>
                                setRatesByUserId((prev) => ({
                                  ...(prev || {}),
                                  [userId]: v,
                                }))
                              }
                                formatter={currencyInputFormatter}
                                parser={currencyInputParser}
                                placeholder="$0.00"
                                title={hasRate ? undefined : 'Rate is required'}
                              />
                            </span>
                          </td>
                          <td>
                            <span className={hasEff ? undefined : 'ppc-project-wizard__invalid'}>
                              <DatePicker
                                oneTap
                                format="yyyy-MM-dd"
                                ranges={[]}
                              value={parseMaybeIsoToDate(effVal)}
                              onChange={(v) => {
                                editedEffectiveDatesRef.current.add(userId);
                                setEffectiveDateByUserId((prev) => ({
                                  ...(prev || {}),
                                  [userId]: normalizeIso10(v),
                                }));
                              }}
                                placeholder="YYYY-MM-DD"
                                style={{ width: 170 }}
                                title={hasEff ? undefined : 'Effective date is required'}
                              />
                            </span>
                          </td>
                          <td className="ppc-project-wizard__table-action">
                            <Button
                              className="ppc-project-wizard__btn ppc-project-wizard__btn--sm"
                              onClick={() => openRateSource(userId)}
                              disabled={isBusy || rateSourceProjectOptions.length === 0}
                              title={`Use ${userName}'s rate from another project`}
                            >
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <i className="bi bi-arrow-repeat" aria-hidden="true" />
                                Use a rate from another project
                              </span>
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {!ratesValid ? (
                <div style={{ marginTop: 12 }}>
                  <Message type="warning" showIcon>
                    Enter a rate and effective date for each person.
                  </Message>
                </div>
              ) : null}

              <div className="ppc-project-wizard__footer">
                <WizardError error={upsertUserRate.error} />
                <Stack justifyContent="space-between" spacing={10}>
                  <Button className="ppc-project-wizard__btn" onClick={() => setStep(2)} disabled={isBusy}>
                    Back
                  </Button>
                  <Button className="ppc-project-wizard__btn ppc-project-wizard__btn--primary" onClick={submitRates} loading={isBusy} disabled={!ratesValid || isBusy}>
                    Finish
                  </Button>
                </Stack>
              </div>
            </section>
          ) : null}
        </div>
      </div>
      </div>

      <Modal
        className="ppc-project-wizard-page"
        open={rateSourceOpen}
        onClose={() => setRateSourceOpen(false)}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>Use a rate from another project</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="ppc-project-wizard__form">
            <div className="ppc-project-wizard__field">
              <div className="ppc-project-wizard__label">Project</div>
              <SelectPicker
                block
                searchable
                cleanable={false}
                data={rateSourceProjectOptions}
                value={Number.isInteger(Number(rateSourceProjectId)) ? Number(rateSourceProjectId) : null}
                onChange={(v) => setRateSourceProjectId(Number.isInteger(v) ? v : null)}
                placeholder="Select a project"
              />
            </div>

            {Number.isInteger(Number(rateSourceProjectId)) ? (
              rateSourceMatches.length === 0 ? (
                <Message type="info" showIcon>
                  {Number.isInteger(rateSourceUserId)
                    ? `${userNameById.get(Number(rateSourceUserId)) || 'This person'} has no rate on that project.`
                    : 'No one on this project has a rate on that project.'}
                </Message>
              ) : (
                <div className="ppc-project-wizard__tablewrap">
                  <table className="ppc-project-wizard__table">
                    <thead>
                      <tr>
                        <th scope="col">Person</th>
                        <th scope="col">Rate to apply</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rateSourceMatches.map((match) => (
                        <tr key={String(match.userId)}>
                          <td>{match.name}</td>
                          <td>{rateCurrencyFormatter.format(Number(match.rate))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton
            onClick={() => setRateSourceOpen(false)}
          >
            Cancel
          </NsiButton>
          {Number.isInteger(rateSourceUserId) ? (
            <NsiButton
              onClick={() => setRateSourceUserId(null)}
              disabled={!Number.isInteger(Number(rateSourceProjectId))}
              title="Apply this project's rates to everyone on the project"
            >
              Apply to all users
            </NsiButton>
          ) : null}
          <NsiButton
            variant="primary"
            onClick={applyRateSource}
            disabled={rateSourceMatches.length === 0}
          >
            {Number.isInteger(rateSourceUserId)
              ? `Apply to ${userNameById.get(Number(rateSourceUserId)) || 'this person'}`
              : `Apply to ${rateSourceMatches.length} ${rateSourceMatches.length === 1 ? 'person' : 'people'}`}
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal open={rightsUserId !== null} onClose={() => setRightsUserId(null)} size="sm">
        <Modal.Header>
          <Modal.Title>{userNameById.get(rightsUserId) || 'Member'}'s rights on this project</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ProjectRightsChecklist
            rights={permissions.projectRights}
            value={rightsDraft}
            onChange={setRightsDraft}
            grantable={Number.isInteger(safeProjectId) ? permissions.grantableRights(safeProjectId) : []}
          />
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={() => setRightsUserId(null)}>
            Cancel
          </NsiButton>
          <NsiButton
            variant="primary"
            onClick={() => {
              setRightsByUserId((prev) => ({ ...prev, [rightsUserId]: rightsDraft }));
              setRightsUserId(null);
            }}
          >
            Done
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal open={logoModalOpen} onClose={() => setLogoModalOpen(false)} size="sm" className="ppc-project-wizard-page">
        <Modal.Header>
          <Modal.Title>Edit project logo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <input
            ref={logoInputRef}
            type="file"
            accept="image/*"
            onChange={onSelectLogoFile}
            style={{ display: 'none' }}
            disabled={isBusy}
          />

          <div className="ppc-project-wizard__photo-modal-wrap">
            <div
              className={`ppc-project-wizard__photo-modal-preview${logoPreviewSrc ? '' : ' is-empty'}`}
              role={!logoPreviewSrc ? 'button' : undefined}
              tabIndex={!logoPreviewSrc ? 0 : undefined}
              onClick={() => {
                if (!logoPreviewSrc) logoInputRef.current?.click();
              }}
              onKeyDown={(e) => {
                if (!logoPreviewSrc && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  logoInputRef.current?.click();
                }
              }}
              onDragOver={(e) => {
                if (!logoPreviewSrc) e.preventDefault();
              }}
              onDrop={(e) => {
                if (logoPreviewSrc) return;
                e.preventDefault();
                applyLogoFile(e.dataTransfer?.files?.[0]);
              }}
              aria-label={!logoPreviewSrc ? 'Upload project logo by clicking or dropping an image' : undefined}
            >
              {logoPreviewSrc ? (
                <img src={logoPreviewSrc} alt="Project logo" />
              ) : (
                <div className="ppc-project-wizard__photo-modal-empty">
                  <i className="bi bi-cloud-arrow-up" aria-hidden="true" />
                  <span>Drag and drop an image here</span>
                  <span className="ppc-project-wizard__photo-modal-empty-sub">or click to browse</span>
                </div>
              )}
            </div>
          </div>

          <div className="ppc-project-wizard__photo-modal-help">PNG/JPG/WebP up to 5MB.</div>

          {logoPreviewSrc ? (
            <div className="ppc-project-wizard__photo-modal-success">
              <i className="bi bi-check-circle-fill" aria-hidden="true" />
              <span>
                {selectedLogoFile
                  ? 'Image added. It uploads when you save this step.'
                  : 'Logo uploaded.'}
              </span>
            </div>
          ) : null}

          {logoPreviewSrc ? (
            <div className="ppc-project-wizard__photo-modal-actions">
              <div className="ppc-project-wizard__photo-modal-actions-group">
                <Button
                  className="ppc-project-wizard__btn"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={isBusy}
                >
                  Replace
                </Button>
                <Button
                  className="ppc-project-wizard__btn ppc-project-wizard__btn--danger"
                  onClick={clearSelectedLogo}
                  disabled={isBusy}
                >
                  Remove
                </Button>
              </div>

              <Button
                className="ppc-project-wizard__btn ppc-project-wizard__btn--primary"
                onClick={() => setLogoModalOpen(false)}
                disabled={isBusy}
              >
                Confirm
              </Button>
            </div>
          ) : null}
        </Modal.Body>
      </Modal>

      <Modal
        className="ppc-project-wizard-page"
        open={addTaskOrderOpen}
        onClose={() => {
          setAddTaskOrderOpen(false);
          resetTaskOrderModal();
        }}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>{taskOrderModalMode === 'edit' ? 'Edit Task Order' : 'Add Task Order'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!Number.isInteger(safeProjectId) ? (
            <Message type="warning" showIcon>
              Create the project first.
            </Message>
          ) : null}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <div className="ppc-project-wizard__label">Task order name</div>
              <Input
                value={newTaskOrderName}
                onChange={setNewTaskOrderName}
                placeholder="e.g. TO-01"
              />
            </div>

            <div className="ppc-project-wizard__grid">
              <div>
                <div className="ppc-project-wizard__label">Start date</div>
                <DatePicker
                  oneTap
                  format="yyyy-MM-dd"
                  value={parseMaybeIsoToDate(newTaskOrderStartDate) || newTaskOrderStartDate}
                  onChange={(v) => {
                    setNewTaskOrderStartDate(v);
                    const s = normalizeIso10(v);
                    const e = normalizeIso10(newTaskOrderEndDate);
                    if (s && e && String(e).localeCompare(String(s)) < 0) {
                      setNewTaskOrderEndDate(v);
                    }
                  }}
                  shouldDisableDate={shouldDisableStartDate}
                  placeholder="YYYY-MM-DD"
                  style={{ width: '100%' }}
                />
                {projectStartIso && projectEndIso ? (
                  <div style={{ fontSize: 12, color: 'var(--pcw-text-muted)', marginTop: 6 }}>
                    Project range: {projectStartIso} → {projectEndIso}
                  </div>
                ) : null}
              </div>

              <div>
                <div className="ppc-project-wizard__label">End date</div>
                <DatePicker
                  oneTap
                  format="yyyy-MM-dd"
                  value={parseMaybeIsoToDate(newTaskOrderEndDate) || newTaskOrderEndDate}
                  onChange={(v) => {
                    setNewTaskOrderEndDate(v);
                    const s = normalizeIso10(newTaskOrderStartDate);
                    const e = normalizeIso10(v);
                    if (s && e && String(e).localeCompare(String(s)) < 0) {
                      setNewTaskOrderStartDate(v);
                    }
                  }}
                  shouldDisableDate={shouldDisableEndDate}
                  placeholder="YYYY-MM-DD"
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            <div>
              <div className="ppc-project-wizard__label">Budget</div>
              <Input
                className="ppc-project-wizard__budget-input"
                value={newTaskOrderBudget}
                onChange={handleNewTaskOrderBudgetChange}
                placeholder="$0"
                inputMode="numeric"
              />
            </div>

            {!canSubmitNewTaskOrder ? (
              <Message type="info" showIcon>
                Enter a name, dates within the project range, and a budget.
              </Message>
            ) : null}

            {createTaskOrder.error ? <WizardError error={createTaskOrder.error} /> : null}
            {updateTaskOrder.error ? <WizardError error={updateTaskOrder.error} /> : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton
            onClick={() => {
              setAddTaskOrderOpen(false);
              resetTaskOrderModal();
            }}
            disabled={createTaskOrder.isPending || updateTaskOrder.isPending}
          >
            Cancel
          </NsiButton>
          <NsiButton
            variant="primary"
            onClick={() => void submitNewTaskOrder()}
            loading={createTaskOrder.isPending || updateTaskOrder.isPending}
            disabled={
              !Number.isInteger(safeProjectId) ||
              !canSubmitNewTaskOrder ||
              createTaskOrder.isPending ||
              updateTaskOrder.isPending
            }
          >
            {taskOrderModalMode === 'edit' ? 'Save' : 'Add'}
          </NsiButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
